import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { REPORT_QUERY, RECORD_QUERY } from '@/lib/report-query';
import { api } from '@/lib/api';

export function useDeleteCommerceOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/api/commerce/orders/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['commerce'] }); },
  });
}

// Live install-verify status. Refetches while a verify panel is open so the
// tenant sees hits land in near-real-time.
export function useCommerceInstallStatus(poll = false) {
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'install-status'],
    queryFn: async () => (await api.get('/api/commerce/install-status')).data,
    refetchInterval: poll ? 5_000 : false,
    staleTime: 0,
  });
}

/**
 * The channel vocabulary (which groups are paid, which are owned), read from
 * the one canon in the API. Pages used to keep their own copy of the paid
 * test and one of them omitted three paid groups.
 */
export function useChannelGroups() {
  return useQuery({
    queryKey: ['commerce', 'channel-groups'],
    queryFn: async () => (await api.get('/api/commerce/channel-groups')).data,
    staleTime: 24 * 3600_000,
  });
}

export function useCommerceOverview(days = 90, range = null) {
  const custom = !!(range && range.from && range.to);
  const params = custom ? { from: range.from, to: range.to } : { days };
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'overview', custom ? `${range.from}_${range.to}` : days],
    queryFn: async () => (await api.get('/api/commerce/overview', { params })).data,
    staleTime: 30_000,
  });
}

export function useCommerceProducts(days = 90) {
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'products', days],
    queryFn: async () => (await api.get('/api/commerce/products', { params: { days } })).data,
    staleTime: 30_000,
  });
}

// One query definition for a model's channel credit, so the single-model table
// and Compare models read the same cache entry and can never show two numbers.
export function attributionQuery(days = 90, model = 'last_click', range = null) {
  const custom = range && range.from && range.to;
  const params = custom ? { model, from: range.from, to: range.to } : { days, model };
  return {
    ...REPORT_QUERY,
    queryKey: ['commerce', 'attribution', custom ? `${range.from}_${range.to}` : days, model],
    queryFn: async () => (await api.get('/api/commerce/attribution', { params })).data,
    staleTime: 30_000,
  };
}

export function useCommerceAttribution(days = 90, model = 'last_click', range = null) {
  return useQuery(attributionQuery(days, model, range));
}

export function useCommerceMetaComparison(days = 90, range = null, model = 'last_click') {
  const custom = range && range.from && range.to;
  const params = { ...(custom ? { from: range.from, to: range.to } : { days }), model };
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'meta-comparison', custom ? `${range.from}_${range.to}` : days, model],
    queryFn: async () => (await api.get('/api/commerce/meta-comparison', { params })).data,
    staleTime: 60_000,
  });
}

// The Orders list's query, shared with the prefetch that warms it.
// How many orders the Orders page loads at most: every order of the window
// on any store we have today, so its tiles add up the whole window, not a
// sample. Shared with the route preload, whose query key must match.
export const ORDERS_PAGE_ROWS = 20000;

export function ordersQuery(limit = 100, missing = null, q = null, win = null, placement = null, journey = null, store = null) {
  // Which kind of journey (devices, browsers, where the ad click landed...),
  // filtered on the server over the order's stored facts (journeyFilters).
  const jf = Object.fromEntries(Object.entries(journey || {}).filter(([, v]) => v && v !== 'all'));
  const days = win && win.days && win.days !== 'all' ? win.days : null;
  const from = win?.range?.from || null;
  const to = win?.range?.to || null;
  return {
    ...REPORT_QUERY,
    queryKey: ['commerce', 'orders', limit, missing || 'all', q || '', days || 'all', from || '', to || '', placement || 'all', jf, store || 'all'],
    // The endpoint returns { orders, total, truncated }; older callers expected a
    // bare array, so normalise here rather than at every call site.
    // `limit` is how many rows the caller wants in all. The server sends at
    // most 1,000 per request, so a larger ask is loaded as pages: the first
    // with the totals, the rest rows only, in parallel. The Orders page asked
    // for 2,000 and got 1,000, then added up those 1,000 under a header that
    // said 3,911 (GirOrganic, all time, 6 Oct 2026).
    queryFn: async () => {
      const PAGE = 1000;
      const base = {
        ...(missing ? { missing } : {}), ...(q ? { q } : {}),
        ...(days ? { days } : {}), ...(from && to ? { from, to } : {}),
        ...(placement ? { placement } : {}),
        ...(store ? { store } : {}),
        ...jf,
      };
      const { data } = await api.get('/api/commerce/orders', { params: { ...base, limit: Math.min(limit, PAGE) } });
      if (Array.isArray(data)) return { orders: data, total: data.length, truncated: false };
      const want = Math.min(limit, data.total || 0);
      if (want <= data.orders.length) return data;
      const offsets = [];
      for (let off = data.orders.length; off < want; off += PAGE) offsets.push(off);
      const rest = await Promise.all(offsets.map((offset) => api.get('/api/commerce/orders', {
        params: { ...base, limit: Math.min(PAGE, want - offset), offset, rows_only: 1 },
      }).then((r) => r.data.orders || [])));
      const orders = data.orders.concat(...rest);
      return { ...data, orders, truncated: (data.total || 0) > orders.length };
    },
    staleTime: 30_000,
    // While a new search is in flight, keep showing the previous rows instead of
    // flashing an empty table on every keystroke.
    placeholderData: (prev) => prev,
  };
}

export function useCommerceOrders(limit = 100, missing = null, q = null, win = null, placement = null, journey = null, store = null) {
  return useQuery(ordersQuery(limit, missing, q, win, placement, journey, store));
}

export function useCommerceOrder(id) {
  return useQuery({
    ...RECORD_QUERY,
    queryKey: ['commerce', 'order', id],
    queryFn: async () => (await api.get(`/api/commerce/orders/${id}`)).data,
    enabled: !!id,
  });
}

// The pages the buyer walked. Its own request, fetched only when the Pages tab
// is opened: one shopper can carry hundreds of views and most visits to an
// order never ask for them.
export function useOrderPageJourney(id, enabled = false) {
  return useQuery({
    ...RECORD_QUERY,
    queryKey: ['commerce', 'order', id, 'page-journey'],
    queryFn: async () => (await api.get(`/api/commerce/orders/${id}/page-journey`)).data,
    enabled: !!id && !!enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCommerceCustomers({ limit = 100, offset = 0, q = '', sort = 'recent',
  channel, recovery, stale_days, from, to, basis, compare, kind = 'buyers', filters = {} } = {}) {
  // Every filter is in BOTH the params and the query key: leave one out of the
  // key and React Query serves the previous filter's cached rows, which looks
  // exactly like a filter that does not work. `filters` is the query builder's
  // set (components/commerce/customer-filters), keyed as one sorted string.
  const params = { limit, offset, q, sort, channel, recovery, stale_days, from, to, basis,
    compare: compare ? '1' : undefined, kind, ...filters };
  const filterKey = Object.keys(filters).sort().map((k) => `${k}=${filters[k]}`).join('&');
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'customers', kind, limit, offset, q, sort, channel, recovery, stale_days, from, to, basis, compare, filterKey],
    queryFn: async () => (await api.get('/api/commerce/customers', { params })).data,
    staleTime: 30_000,
  });
}

export function useCommerceCustomer(id) {
  return useQuery({
    ...RECORD_QUERY,
    queryKey: ['commerce', 'customer', id],
    queryFn: async () => (await api.get(`/api/commerce/customers/${id}`)).data,
    enabled: !!id,
  });
}

/** What this person does on the store: last seen, visits, products, intent band, one action. */
export function useCommerceCustomerBehaviour(id) {
  return useQuery({
    ...RECORD_QUERY,
    queryKey: ['commerce', 'customer', id, 'behaviour'],
    queryFn: async () => (await api.get(`/api/commerce/customers/${id}/behaviour`)).data,
    enabled: !!id,
  });
}

/** Their story: visits, orders and messages as one feed, newest first. */
export function useCommerceCustomerStory(id, limit = 200) {
  return useQuery({
    ...RECORD_QUERY,
    queryKey: ['commerce', 'customer', id, 'story', limit],
    queryFn: async () => (await api.get(`/api/commerce/customers/${id}/story`, { params: { limit } })).data,
    enabled: !!id,
  });
}

/** The store's display currency - one tiny query, not an overview. */
export function useStoreCurrency() {
  return useQuery({
    queryKey: ['commerce', 'currency'],
    queryFn: async () => (await api.get('/api/commerce/currency')).data,
    staleTime: 60 * 60_000,
  });
}

/**
 * Who came back to the store: past buyers, known people, returning and new
 * visitors for today, yesterday and the last 7 days, and the latest past
 * buyers seen (api commerce/returningVisitors). `to` pins "today" to a date
 * (YYYY-MM-DD, for a dataset that stops in the past); omitted, the API uses
 * the workspace's own today.
 */
export function useReturningVisitors(to = null) {
  return useQuery({
    ...REPORT_QUERY,
    queryKey: ['commerce', 'returning-visitors', to || 'today'],
    queryFn: async () => (await api.get('/api/commerce/returning-visitors', { params: to ? { to } : {} })).data,
    staleTime: 60_000,
  });
}
